/**
 * Biome kits (03 §4): how each terrain answers the generic needs of 03 §2.1 - low cover,
 * tall vegetation, the depression network, the water barrier, the soft interior - so that
 * any archetype can be built in any theatre.
 */

import type { Biome, PropKind, WeatherKind } from '../sim/types';
import type { Range } from './tuning';
import type { LineKind } from './types';

export interface ScatterKind {
  kind: PropKind;
  weight: number;
  scale: Range;
}

export type EmplacementKind = 'mgNest' | 'bunker' | 'mannedTurret' | 'turretBunker';

export interface BiomeKit {
  biome: Biome;
  /** Boulders, bushes, stumps, drifts: hides a crawler. */
  lowCover: readonly ScatterKind[];
  /** Fir stands, palms, woodland: planted in clumps. */
  tall: readonly ScatterKind[];
  /** Share of scatter that is tall vegetation. */
  tallShare: number;
  /** Short cover runs dotted about the approaches (hedgerows, field walls, sandbag lines). */
  coverRun: { kind: 'hedge' | 'wall' | 'sandbags'; perBand: number } | null;
  /** Ravines, trenches and gullies, ditches: width and depth in metres. */
  depression: { width: Range; depth: Range };
  /** What the linear obstacles on band boundaries are made of, weighted. */
  lines: ReadonlyArray<readonly [LineKind, number]>;
  /** The Radar mission's barrier: frozen river, wadi, river. A bridge appears from this threat. */
  water: { surface: 'ice' | 'water'; bridgeFromThreat: number };
  /** The soft interior: where the officer sleeps and where a uniform can be had (02 §6.4). */
  shelter: 'tent' | 'guardHouse';
  ponds: Range;
  /** Desert ponds are oases: palms gather round them. */
  oasis: boolean;
  storm: WeatherKind;
  emplacements: Record<EmplacementKind, number>;
  proximityPosts: boolean;
  tankTraps: boolean;
  /** Multiplier on scatter density. */
  density: number;
}

const DESERT: BiomeKit = {
  biome: 'desert',
  lowCover: [
    { kind: 'rock', weight: 4, scale: [0.8, 1.5] },
    { kind: 'shrub', weight: 3, scale: [0.9, 1.2] },
    { kind: 'cactus', weight: 3, scale: [0.85, 1.2] },
  ],
  tall: [
    { kind: 'palm', weight: 2, scale: [0.9, 1.2] },
    { kind: 'deadTree', weight: 1, scale: [0.9, 1.1] },
  ],
  tallShare: 0.12,
  coverRun: { kind: 'sandbags', perBand: 1 },
  depression: { width: [2.2, 2.6], depth: [1.25, 1.35] },
  lines: [['trench', 4], ['wire', 3], ['wall', 2]],
  water: { surface: 'water', bridgeFromThreat: 1 },
  shelter: 'tent',
  ponds: [0, 1],
  oasis: true,
  storm: 'sandstorm',
  emplacements: { mgNest: 5, bunker: 3, mannedTurret: 0.5, turretBunker: 0.5 },
  proximityPosts: false,
  tankTraps: false,
  density: 0.9,
};

const TEMPERATE: BiomeKit = {
  biome: 'temperate',
  lowCover: [
    { kind: 'stump', weight: 3, scale: [0.9, 1.3] },
    { kind: 'shrub', weight: 3, scale: [0.9, 1.25] },
    { kind: 'rock', weight: 1.5, scale: [0.8, 1.3] },
  ],
  tall: [
    { kind: 'broadleaf', weight: 4, scale: [0.9, 1.3] },
    { kind: 'pine', weight: 1, scale: [0.9, 1.2] },
  ],
  tallShare: 0.3,
  coverRun: { kind: 'hedge', perBand: 2 },
  depression: { width: [2.0, 2.4], depth: [1.1, 1.25] },
  lines: [['trench', 3], ['hedge', 3], ['wire', 2], ['wall', 2]],
  water: { surface: 'water', bridgeFromThreat: 1 },
  shelter: 'tent',
  ponds: [1, 3],
  oasis: false,
  // ROUND5: temperate's storm used to be plain `rain`, which a calm day also brings - so the Storm
  // card changed nothing here but the reward. A thunderstorm is its own weather, and blows like one.
  storm: 'thunderstorm',
  emplacements: { mgNest: 4, bunker: 2.5, mannedTurret: 2, turretBunker: 2 },
  proximityPosts: true,
  tankTraps: true,
  density: 1,
};

const ARCTIC: BiomeKit = {
  biome: 'arctic',
  lowCover: [
    { kind: 'snowDrift', weight: 4, scale: [0.85, 1.3] },
    { kind: 'rock', weight: 2.5, scale: [0.8, 1.4] },
  ],
  tall: [
    { kind: 'pine', weight: 5, scale: [0.9, 1.35] },
    { kind: 'deadTree', weight: 1, scale: [0.9, 1.1] },
  ],
  tallShare: 0.28,
  coverRun: { kind: 'wall', perBand: 1 },
  depression: { width: [2.6, 3.0], depth: [1.35, 1.5] },
  lines: [['trench', 4], ['wire', 3], ['wall', 2]],
  water: { surface: 'ice', bridgeFromThreat: 6 },
  shelter: 'guardHouse',
  ponds: [1, 2],
  oasis: false,
  storm: 'blizzard',
  emplacements: { mgNest: 4, bunker: 3.5, mannedTurret: 0.5, turretBunker: 1 },
  proximityPosts: false,
  tankTraps: false,
  density: 0.95,
};

export const BIOME_KITS: Record<Biome, BiomeKit> = { desert: DESERT, temperate: TEMPERATE, arctic: ARCTIC };
